import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  deploySignal,
  deployEaSignal,
  getTelegramStatus,
  startTelegramLink,
  saveNotificationPrefs,
  sendTestNotification,
  CHANNEL_LABELS,
} from './signalDataFeeds';
import { fetchSavedTesterStrategy } from './strategyTesterDataFeeds';

// Deploy wizard — four steps: confirm the source, configure it, set up notifications, review.
//
// ONE wizard for both kinds of signal. A Builder strategy and an imported MQL5 EA differ only in
// where the logic comes from; everything after that — the deposit, the visibility, the alert
// channels, the review — is identical, and so is the deployment they produce. Two separate wizards
// would drift apart and teach users two flows for one job.
//
// An EA is never uploaded HERE. It arrives already analysed from the import page (`eaContext`),
// which is where the user chose its symbol, timeframe and inputs and — recommended — ran a
// backtest. Re-asking for the files would mean re-analysing and re-choosing all of it.
//
// The notifications step deliberately comes BEFORE the review rather than after deploying.
// Someone who deploys and then discovers their alerts never arrive has already lost trust, so
// the flow makes them prove the channel works (via the test button) while they still have the
// wizard's attention.

const STEPS = ['Source', 'Configure', 'Notifications', 'Review'];

const NOTIFY_OPTIONS = [
  { id: 'trade_open', label: 'Trade opened', hint: 'A new entry fires' },
  { id: 'trade_close', label: 'Trade closed', hint: 'An exit with the result' },
  { id: 'position_update', label: 'Stop / target moved', hint: 'Trailing and breakeven moves' },
];

// Turns the locked rule set into one plain-English sentence, so the last thing seen before
// deploying is a description of what will actually be traded rather than a wall of JSON.
function describeStrategy(strategy) {
  if (!strategy) return '';
  const parts = [];
  const rules = strategy.rules || {};
  if (rules.indicatorEntriesEnabled) parts.push('indicator conditions');
  if (rules.rangeBreakoutEnabled) parts.push('range breakout');
  if (rules.structuralBreakoutEnabled) parts.push(`${rules.structuralBreakoutRef || 'structural'} breakout`);
  if (rules.candlePatternEntryEnabled) parts.push('candle patterns');
  if (rules.scriptEnabled) parts.push('a VixScript rule');
  if (rules.gridEnabled) parts.push('a grid');
  const entry = parts.length ? parts.join(' or ') : 'its saved entry rules';

  // Field names verified against DEFAULT_RULES in testerBacktestEngine — trailing and
  // breakeven are driven by a non-zero point value, not by a boolean flag.
  const protection = [];
  if (Number(rules.stopLoss) > 0) protection.push(`${rules.stopLoss}pt stop`);
  if (Number(rules.takeProfit) > 0) protection.push(`${rules.takeProfit}pt target`);
  if (Number(rules.trailingStop) > 0) protection.push(`a ${rules.trailingStop}pt trailing stop`);
  if (Number(rules.breakEvenTrigger) > 0) protection.push(`breakeven at ${rules.breakEvenTrigger}pt`);
  if (rules.structureTrailingEnabled) protection.push('structure trailing');
  if (rules.partialTpEnabled) protection.push(`${rules.partialTpPercent || 50}% partial take-profit`);

  return `Trades ${strategy.symbol} on ${strategy.timeframe} using ${entry}`
    + (protection.length ? `, protected by ${protection.join(', ')}.` : '.');
}

function DeploySignalWizard({ strategies, initialStrategyId, eaContext, onClose, onDeployed }) {
  const [step, setStep] = useState(0);
  // The caller decides which kind of signal this is: an EA when it hands over an analysed bundle,
  // a saved strategy otherwise.
  const source = eaContext ? 'ea' : 'strategy';
  const [strategyId, setStrategyId] = useState(initialStrategyId || strategies?.[0]?.id || null);
  const [name, setName] = useState('');
  const [virtualDeposit, setVirtualDeposit] = useState(10000);
  const [visibility, setVisibility] = useState('private');
  const [channels, setChannels] = useState(['inapp']);
  const [notifyOn, setNotifyOn] = useState(['trade_open', 'trade_close']);
  const [telegram, setTelegram] = useState({ linked: false, configured: false });
  const [testState, setTestState] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // The saved-strategy LIST endpoint returns metadata only — no rules — so the full strategy
  // is fetched on selection. Without it the plain-English summary can only say "its saved
  // entry rules", which defeats the point of the review step.
  const [detail, setDetail] = useState(null);

  const listEntry = useMemo(
    () => (strategies || []).find((item) => item.id === strategyId) || null,
    [strategies, strategyId],
  );

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    if (!strategyId) return undefined;
    fetchSavedTesterStrategy(strategyId, localStorage.getItem('userToken'))
      .then((payload) => { if (!cancelled) setDetail(payload?.strategy || null); })
      .catch(() => { /* fall back to the list entry's metadata */ });
    return () => { cancelled = true; };
  }, [strategyId]);

  // Detail when it has arrived, list metadata until then, so nothing flickers empty.
  const strategy = detail || listEntry;

  useEffect(() => {
    if (listEntry && !name) setName(listEntry.name || '');
  }, [listEntry, name]);

  const refreshTelegram = useCallback(async () => {
    try {
      setTelegram(await getTelegramStatus());
    } catch (err) {
      setTelegram({ linked: false, configured: false });
    }
  }, []);

  useEffect(() => { refreshTelegram(); }, [refreshTelegram]);

  // Name the signal after the EA by default, so the review step is not blank.
  useEffect(() => {
    if (eaContext && !name) setName(String(eaContext.entry || '').replace(/\.mq5$/i, ''));
  }, [eaContext, name]);

  const toggle = (list, setList, value) => {
    setList(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
  };

  const handleConnectTelegram = async () => {
    try {
      const { url } = await startTelegramLink();
      window.open(url, '_blank', 'noopener');
      // The user completes the link inside Telegram, so poll briefly for the binding rather
      // than making them refresh the page.
      let attempts = 0;
      const timer = setInterval(async () => {
        attempts += 1;
        const status = await getTelegramStatus().catch(() => null);
        if (status?.linked || attempts > 30) {
          clearInterval(timer);
          if (status) setTelegram(status);
        }
      }, 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTest = async (channel) => {
    setTestState((prev) => ({ ...prev, [channel]: { state: 'sending' } }));
    try {
      await sendTestNotification(channel);
      setTestState((prev) => ({ ...prev, [channel]: { state: 'sent' } }));
    } catch (err) {
      setTestState((prev) => ({ ...prev, [channel]: { state: 'failed', message: err.message } }));
    }
  };

  const handleDeploy = async () => {
    setBusy(true);
    setError('');
    try {
      const result = source === 'ea'
        ? await deployEaSignal({
          files: eaContext.files,
          entry: eaContext.entry,
          name,
          symbol: eaContext.symbol,
          timeframe: eaContext.timeframe,
          // The inputs the user configured on the import page — and backtested, if they ran one.
          // Deploying with different inputs than were tested would make the backtest meaningless.
          inputs: eaContext.inputs || {},
          leverage: eaContext.leverage,
          virtualDeposit: Number(virtualDeposit) || 10000,
          visibility,
        })
        : await deploySignal({
          strategyId,
          name,
          symbol: strategy?.symbol,
          timeframe: strategy?.timeframe,
          virtualDeposit: Number(virtualDeposit) || 10000,
          visibility,
        });
      // Preferences are saved per deployment so different signals can alert differently.
      await saveNotificationPrefs({
        deploymentId: result.deployment.id,
        channels,
        notifyOn,
      }).catch(() => {});
      onDeployed?.(result.deployment);
    } catch (err) {
      setError(err.code === 'VIX_PREMIUM_REQUIRED'
        ? 'Deploying a live signal requires VixPremium.'
        : err.message);
      setBusy(false);
    }
  };

  const canAdvance = () => {
    // An EA arrives already analysed and configured, so there is nothing to gate on here.
    if (step === 0) return source === 'ea' ? !!eaContext.files?.length : !!strategyId;
    if (step === 1) return Number(virtualDeposit) >= 100;
    return true;
  };

  return (
    <div className="signal-wizard-backdrop" role="dialog" aria-modal="true" aria-label="Deploy a live signal">
      <div className="signal-wizard">
        <header className="signal-wizard-head">
          <div>
            <h3>Deploy live signal</h3>
            <p className="muted-text">
              Your strategy runs on live market data in the cloud. No MetaTrader required.
            </p>
          </div>
          <button type="button" className="ghost-button compact-button" onClick={onClose}>Close</button>
        </header>

        <ol className="signal-wizard-steps">
          {STEPS.map((label, index) => (
            <li
              key={label}
              className={`${index === step ? 'active' : ''} ${index < step ? 'done' : ''}`}
            >
              <span className="signal-step-dot">{index < step ? '✓' : index + 1}</span>
              {label}
            </li>
          ))}
        </ol>

        <div className="signal-wizard-body">
          {step === 0 && (
            <div className="signal-step">
              {source === 'strategy' ? (
                <>
                  <label className="signal-field">
                    <span>Strategy</span>
                    <select value={strategyId || ''} onChange={(event) => setStrategyId(Number(event.target.value))}>
                      {(strategies || []).map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} — {item.symbol} {item.timeframe}
                        </option>
                      ))}
                    </select>
                  </label>
                  {!strategies?.length && (
                    <p className="muted-text">
                      You have no saved strategies yet. Build one in the Builder and save it first.
                    </p>
                  )}
                  {strategy && (
                    <div className="signal-summary-card">
                      <h4>{strategy.name}</h4>
                      <p className="muted-text">{describeStrategy(strategy)}</p>
                      <div className="signal-chip-row">
                        <span className="signal-chip">{strategy.symbol}</span>
                        <span className="signal-chip">{strategy.timeframe}</span>
                        {strategy.dataSource ? <span className="signal-chip">{strategy.dataSource}</span> : null}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Everything here was decided on the import page. It is shown, not re-asked. */}
                  <div className="signal-summary-card">
                    <h4>{eaContext.entry}</h4>
                    <p className="muted-text">
                      {eaContext.files?.length || 0} file{eaContext.files?.length === 1 ? '' : 's'}
                      {' · '}
                      {eaContext.report?.execution?.mode === 'tick'
                        ? 'evaluates on every tick'
                        : 'evaluates on each new bar'}
                    </p>
                    <div className="signal-chip-row">
                      <span className="signal-chip">{eaContext.symbol}</span>
                      <span className="signal-chip">{eaContext.timeframe}</span>
                      {eaContext.backtest ? (
                        <span className="signal-pill ok">Backtested</span>
                      ) : (
                        <span className="signal-pill">Not backtested</span>
                      )}
                    </div>
                  </div>

                  {eaContext.backtest ? (
                    <dl className="signal-review-list">
                      <div><dt>Backtest trades</dt><dd>{eaContext.backtest.trades}</dd></div>
                      <div>
                        <dt>Net profit</dt>
                        <dd>{eaContext.backtest.netProfit >= 0 ? '+' : ''}{eaContext.backtest.netProfit}</dd>
                      </div>
                      <div><dt>Profit factor</dt><dd>{eaContext.backtest.profitFactor ?? '—'}</dd></div>
                    </dl>
                  ) : (
                    // Not a blocker — the user chose to skip it — but the last chance to say that
                    // deploying something never tested is a decision, not an oversight.
                    <p className="muted-text">
                      You skipped the backtest. This EA will run live without ever having been
                      checked against history on this platform. You can close this, run a backtest,
                      and deploy afterwards.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="signal-step">
              <label className="signal-field">
                <span>Signal name</span>
                <input value={name} onChange={(event) => setName(event.target.value)} maxLength={80} />
              </label>
              <label className="signal-field">
                <span>Virtual deposit (USD)</span>
                <input
                  type="number"
                  min="100"
                  step="100"
                  value={virtualDeposit}
                  onChange={(event) => setVirtualDeposit(event.target.value)}
                />
                <small className="muted-text">
                  The starting balance of the cloud demo account this signal trades. It only
                  affects position sizing and the equity curve.
                </small>
              </label>
              <label className="signal-field">
                <span>Visibility</span>
                <select value={visibility} onChange={(event) => setVisibility(event.target.value)}>
                  <option value="private">Private — only you</option>
                  <option value="unlisted">Unlisted — anyone with the link</option>
                  <option value="public">Public — listed for other traders</option>
                </select>
                <small className="muted-text">
                  You can change this at any time. Public signals show their track record but
                  never your strategy rules.
                </small>
              </label>
            </div>
          )}

          {step === 2 && (
            <div className="signal-step">
              <div className="signal-field">
                <span>Where should signals go?</span>
                <div className="signal-channel-grid">
                  {Object.entries(CHANNEL_LABELS).map(([id, label]) => {
                    const active = channels.includes(id);
                    const needsLink = id === 'telegram' && !telegram.linked;
                    return (
                      <div key={id} className={`signal-channel ${active ? 'active' : ''}`}>
                        <label>
                          <input
                            type="checkbox"
                            checked={active}
                            onChange={() => toggle(channels, setChannels, id)}
                          />
                          <strong>{label}</strong>
                        </label>
                        {id === 'telegram' && (
                          telegram.configured ? (
                            telegram.linked ? (
                              <span className="signal-pill ok">Connected</span>
                            ) : (
                              <button type="button" className="primary-cta compact-button" onClick={handleConnectTelegram}>
                                Connect
                              </button>
                            )
                          ) : <span className="signal-pill">Unavailable</span>
                        )}
                        {active && !needsLink && (
                          <button
                            type="button"
                            className="ghost-button compact-button"
                            onClick={() => handleTest(id)}
                          >
                            {testState[id]?.state === 'sending' ? 'Sending…'
                              : testState[id]?.state === 'sent' ? 'Sent ✓' : 'Send test'}
                          </button>
                        )}
                        {testState[id]?.state === 'failed' && (
                          <small className="signal-error">{testState[id].message}</small>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="signal-field">
                <span>Notify me when…</span>
                <div className="signal-toggle-list">
                  {NOTIFY_OPTIONS.map((option) => (
                    <label key={option.id} className="signal-toggle">
                      <input
                        type="checkbox"
                        checked={notifyOn.includes(option.id)}
                        onChange={() => toggle(notifyOn, setNotifyOn, option.id)}
                      />
                      <span>
                        <strong>{option.label}</strong>
                        <small className="muted-text">{option.hint}</small>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="signal-step">
              <div className="signal-summary-card">
                <h4>{name || (source === 'ea' ? eaContext.entry : strategy?.name)}</h4>
                <p>
                  {source === 'ea'
                    ? `Runs the ${eaContext.entry} Expert Advisor on ${eaContext.symbol} ${eaContext.timeframe}.`
                    : describeStrategy(strategy)}
                </p>
                <dl className="signal-review-list">
                  <div>
                    <dt>Market</dt>
                    <dd>
                      {source === 'ea'
                        ? `${eaContext.symbol} ${eaContext.timeframe}`
                        : `${strategy?.symbol} ${strategy?.timeframe}`}
                    </dd>
                  </div>
                  <div><dt>Virtual deposit</dt><dd>${Number(virtualDeposit).toLocaleString()}</dd></div>
                  <div><dt>Visibility</dt><dd>{visibility}</dd></div>
                  <div>
                    <dt>Alerts</dt>
                    <dd>{channels.map((id) => CHANNEL_LABELS[id]).join(', ') || 'None'}</dd>
                  </div>
                </dl>
              </div>
              {/* Said before deploying, not after. An EA that needs per-tick evaluation behaves
                  differently on a bar cadence, and the person deploying it should know which of
                  the two they are getting. */}
              {source === 'ea' && eaContext.report?.execution?.mode === 'tick' && (
                <p className="muted-text">
                  This EA evaluates on every tick. Unless tick-driven sessions are enabled for your
                  account, the signal advances on closed bars, so an entry or a trailing stop that
                  depends on price moving inside a bar can differ from the same EA on a terminal.
                </p>
              )}
              <p className="muted-text">
                {source === 'ea'
                  ? `Your source is compiled to a cloud version and discarded — only the compiled
                     program is kept. This is a simulated account for tracking and alerts; it does
                     not place real trades.`
                  : `Signals are evaluated when each ${strategy?.timeframe} candle closes — exactly
                     like your backtest and your exported EA. This is a simulated account for
                     tracking and alerts; it does not place real trades.`}
              </p>
            </div>
          )}

          {error && <p className="signal-error">{error}</p>}
        </div>

        <footer className="signal-wizard-foot">
          <button
            type="button"
            className="ghost-button compact-button"
            disabled={step === 0 || busy}
            onClick={() => setStep((value) => Math.max(0, value - 1))}
          >
            Back
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              className="primary-cta compact-button"
              disabled={!canAdvance()}
              onClick={() => setStep((value) => value + 1)}
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              className="primary-cta compact-button"
              disabled={busy}
              onClick={handleDeploy}
            >
              {busy ? 'Deploying…' : 'Deploy signal'}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}

export default DeploySignalWizard;
